import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Offer, Listing } from '../types';
import { calculatePtcScore, buildPtcInputFromOffer } from '../services/ptcCalculator';
import './OfferCompareModal.css';

interface Props {
  offers: Offer[];
  listings: Record<string, Listing>;
  onClose: () => void;
}

const fmt$ = (v: number | null | undefined) =>
  v != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v)
    : '—';

const fmtDate = (s: string | null | undefined) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getLiveScore = (offer: Offer): number | null => {
  if (offer.extractedData) {
    const input = buildPtcInputFromOffer(offer.extractedData, offer.lenderExperienceYears ?? undefined);
    return Math.round(calculatePtcScore(input).ptcScore);
  }
  return typeof offer.ptcScore === 'number' ? Math.round(offer.ptcScore) : null;
};

const getScoreClass = (score: number) => {
  if (score >= 70) return 'excellent';
  if (score >= 50) return 'good';
  if (score >= 30) return 'fair';
  return 'poor';
};

const bestIdx = (values: (number | null)[], prefer: 'max' | 'min'): Set<number> => {
  const valid = values.filter((v): v is number => v !== null);
  if (valid.length < 2) return new Set();
  const target = prefer === 'max' ? Math.max(...valid) : Math.min(...valid);
  return new Set(values.reduce<number[]>((acc, v, i) => (v === target ? [...acc, i] : acc), []));
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  cash_with_new_mortgage: 'Mortgage',
  cash_with_land_contract: 'Land Contract',
  assumption: 'Assumption',
  seller_financing: 'Seller Financing',
  other: 'Other',
};
const fmtPayment = (v: string | null | undefined) =>
  v ? (PAYMENT_METHOD_LABELS[v] ?? v) : '—';

const ContingencyTag: React.FC<{ good: boolean | null; goodLabel?: string; badLabel?: string }> = ({
  good, goodLabel = 'No', badLabel = 'Yes',
}) => {
  if (good === null || good === undefined) return <span className="cmp-tag cmp-tag-good">No</span>;
  return <span className={`cmp-tag ${good ? 'cmp-tag-good' : 'cmp-tag-bad'}`}>{good ? goodLabel : badLabel}</span>;
};

const SectionRow: React.FC<{ label: string; colCount: number }> = ({ label, colCount }) => (
  <tr className="cmp-section-row">
    <td colSpan={colCount + 1}>{label}</td>
  </tr>
);

const OfferCompareModal: React.FC<Props> = ({ offers, listings, onClose }) => {
  const navigate = useNavigate();
  const n = offers.length;

  const scores = offers.map(getLiveScore);
  const bestScoreIdx = bestIdx(scores, 'max');

  const prices = offers.map(o => o.extractedData?.transaction?.final_sales_price ?? null);
  const bestPriceIdx = bestIdx(prices, 'max');

  // Listing price per offer (may differ if offers are on different listings)
  const listingPrices = offers.map(o => (o.listingId ? listings[o.listingId] : null)?.listingPrice ?? null);

  const downPayPcts = offers.map(o => o.extractedData?.financing?.down_payment_percent ?? null);
  const bestDownIdx = bestIdx(downPayPcts, 'max');

  const earnests = offers.map(o => o.extractedData?.transaction?.earnest_money_deposit ?? null);
  const bestEarnestIdx = bestIdx(earnests, 'max');

  const ltvs = offers.map(o => {
    const price = o.extractedData?.transaction?.final_sales_price;
    const loan = o.extractedData?.financing?.mortgage_amount;
    if (!price || !loan) return null;
    return Math.round((loan / price) * 1000) / 10;
  });
  const bestLtvIdx = bestIdx(ltvs, 'min');

  const closingTs = offers.map(o => {
    const s = o.extractedData?.transaction?.closing_date;
    if (!s) return null;
    const t = new Date(s).getTime();
    return isNaN(t) ? null : t;
  });
  const bestClosingIdx = bestIdx(closingTs, 'min');

  return (
    <div className="cmp-overlay" onClick={onClose}>
      <div className="cmp-modal" onClick={e => e.stopPropagation()}>

        {/* ── Modal Header ── */}
        <div className="cmp-header">
          <div>
            <h2 className="cmp-title">Offer Comparison</h2>
            <p className="cmp-subtitle">Comparing {n} offer{n !== 1 ? 's' : ''} side by side</p>
          </div>
          <button className="cmp-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Single unified table — score row is in <thead> so columns align exactly ── */}
        <div className="cmp-table-wrapper">
          <table className="cmp-table">
            {/* colgroup locks column widths for every row including the score header */}
            <colgroup>
              <col className="cmp-col-label" />
              {offers.map(o => <col key={o.id} className="cmp-col-offer" />)}
            </colgroup>

            <thead>
              {/* ── Row 1: PTC Score cards ── */}
              <tr className="cmp-score-header-row">
                <th className="cmp-score-label-th">
                  <div className="cmp-score-label-text">PTC Score</div>
                  <div className="cmp-score-hint">0 – 100</div>
                </th>
                {offers.map((offer, i) => {
                  const score = scores[i];
                  const listing = offer.listingId ? listings[offer.listingId] : null;
                  const isBest = bestScoreIdx.has(i);
                  return (
                    <th key={offer.id} className={`cmp-score-cell${isBest ? ' cmp-score-best' : ''}`}>
                      <div className="cmp-offer-meta">
                        <span className="cmp-offer-num">Offer {i + 1}</span>
                        {listing && <span className="cmp-offer-addr">{listing.address}</span>}
                        <span className="cmp-offer-date">
                          {offer.submissionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <div className={`cmp-score-circle${score !== null ? ` ${getScoreClass(score)}` : ' unknown'}`}>
                        <span className="cmp-score-val">{score !== null ? score : '—'}</span>
                        <span className="cmp-score-sub">PTC</span>
                      </div>
                      {isBest && <span className="cmp-top-badge">Top Score</span>}
                    </th>
                  );
                })}
              </tr>

              {/* ── Row 2: Column labels ── */}
              <tr className="cmp-col-label-row">
                <th className="cmp-field-label-th">Field</th>
                {offers.map((offer, i) => (
                  <th key={offer.id} className={`cmp-col-label-th${bestScoreIdx.has(i) ? ' cmp-col-best-th' : ''}`}>
                    <span className="cmp-th-num">Offer {i + 1}</span>
                    {offer.extractedData?.agents?.buyer_broker?.agent_name && (
                      <span className="cmp-th-sub">{offer.extractedData.agents.buyer_broker.agent_name}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>

              {/* ── Financial ── */}
              <SectionRow label="Financial Summary" colCount={n} />

              <tr>
                <td className="cmp-row-label">Listing Price (LP)</td>
                {offers.map((o, i) => (
                  <td key={o.id}>
                    <span className="cmp-val-money cmp-lp-ref">{fmt$(listingPrices[i])}</span>
                  </td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Purchase Price</td>
                {offers.map((o, i) => {
                  const price = prices[i];
                  const lp = listingPrices[i];
                  const delta = price != null && lp != null ? price - lp : null;
                  const pct = delta != null && lp ? (delta / lp) * 100 : null;
                  const ec = o.extractedData?.escalation_clause;
                  const ag = o.extractedData?.appraisal_guarantee;
                  return (
                    <td key={o.id} className={bestPriceIdx.has(i) ? 'cmp-cell-best' : ''}>
                      <span className="cmp-val-money">{fmt$(price)}</span>
                      {bestPriceIdx.has(i) && <span className="cmp-cell-tag">Highest</span>}
                      {delta !== null && (
                        <span className={`cmp-lp-delta ${delta > 0 ? 'cmp-lp-above' : delta < 0 ? 'cmp-lp-below' : 'cmp-lp-at'}`}>
                          {delta > 0 ? '↑' : delta < 0 ? '↓' : '='}{' '}
                          {delta === 0
                            ? 'At LP'
                            : `${fmt$(Math.abs(delta))}${pct !== null ? ` (${Math.abs(pct).toFixed(1)}%)` : ''} ${delta > 0 ? 'above' : 'below'} LP`}
                        </span>
                      )}
                      {ec?.present && (
                        <div className="cmp-clause-inline">
                          <span className="cmp-clause-badge cmp-clause-yes">⚡ Escalation</span>
                          {ec.max_cap != null && <span className="cmp-clause-detail">Cap: {fmt$(ec.max_cap)}</span>}
                          {ec.increment_amount != null && <span className="cmp-clause-detail">+{fmt$(ec.increment_amount)}</span>}
                        </div>
                      )}
                      {ag?.present && (
                        <div className="cmp-clause-inline">
                          <span className="cmp-clause-badge cmp-clause-yes">🛡️ Appraisal Gap</span>
                          {ag.coverage_amount != null
                            ? <span className="cmp-clause-detail">Up to {fmt$(ag.coverage_amount)}</span>
                            : <span className="cmp-clause-detail cmp-sub">Amount TBD</span>}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>

              <tr>
                <td className="cmp-row-label">Down Payment</td>
                {offers.map((o, i) => {
                  const amt = o.extractedData?.financing?.down_payment_amount ?? null;
                  const pct = o.extractedData?.financing?.down_payment_percent ?? null;
                  return (
                    <td key={o.id} className={bestDownIdx.has(i) ? 'cmp-cell-best' : ''}>
                      {amt != null ? (
                        <span className="cmp-val-money">
                          {fmt$(amt)}{pct != null && <span className="cmp-pct"> ({pct.toFixed(1)}%)</span>}
                        </span>
                      ) : pct != null ? (
                        <span className="cmp-val-money">{pct.toFixed(1)}%</span>
                      ) : '—'}
                      {bestDownIdx.has(i) && <span className="cmp-cell-tag">Highest %</span>}
                    </td>
                  );
                })}
              </tr>

              <tr>
                <td className="cmp-row-label">Loan Amount</td>
                {offers.map((o, i) => (
                  <td key={o.id}>
                    <span className="cmp-val-money">{fmt$(o.extractedData?.financing?.mortgage_amount ?? null)}</span>
                  </td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">LTV Ratio</td>
                {offers.map((o, i) => (
                  <td key={o.id} className={bestLtvIdx.has(i) ? 'cmp-cell-best' : ''}>
                    {ltvs[i] != null ? `${ltvs[i]}%` : '—'}
                    {bestLtvIdx.has(i) && <span className="cmp-cell-tag">Lowest</span>}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Earnest Money</td>
                {offers.map((o, i) => (
                  <td key={o.id} className={bestEarnestIdx.has(i) ? 'cmp-cell-best' : ''}>
                    <span className="cmp-val-money">{fmt$(earnests[i])}</span>
                    {bestEarnestIdx.has(i) && <span className="cmp-cell-tag">Highest</span>}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Seller Concession</td>
                {offers.map((o, i) => {
                  const amt = o.extractedData?.financing?.seller_concession_amount ?? null;
                  const pct = o.extractedData?.financing?.seller_concession_percent ?? null;
                  return (
                    <td key={o.id}>
                      {amt != null
                        ? <span className="cmp-val-money">{fmt$(amt)}{pct != null && <span className="cmp-pct"> ({pct.toFixed(1)}%)</span>}</span>
                        : pct != null
                          ? <span>{pct.toFixed(1)}%</span>
                          : <span className="cmp-none">None</span>}
                    </td>
                  );
                })}
              </tr>

              {/* ── Financing ── */}
              <SectionRow label="Financing" colCount={n} />

              <tr>
                <td className="cmp-row-label">Payment Method</td>
                {offers.map((o, i) => (
                  <td key={o.id}>{fmtPayment(o.extractedData?.financing?.payment_method)}</td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Loan Type</td>
                {offers.map((o, i) => (
                  <td key={o.id}>{o.extractedData?.financing?.mortgage_type || '—'}</td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Pre-Approval Max</td>
                {offers.map((o, i) => (
                  <td key={o.id}>
                    <span className="cmp-val-money">{fmt$(o.extractedData?.pre_approval?.max_purchase_price ?? null)}</span>
                  </td>
                ))}
              </tr>

              {/* ── Contingencies ── */}
              <SectionRow label="Contingencies" colCount={n} />

              <tr>
                <td className="cmp-row-label">Appraisal</td>
                {offers.map((o, i) => {
                  const val = o.extractedData?.contingencies?.appraisal ?? null;
                  return (
                    <td key={o.id}>
                      <ContingencyTag good={val === null ? null : !val} />
                    </td>
                  );
                })}
              </tr>

              <tr>
                <td className="cmp-row-label">Home Inspection</td>
                {offers.map((o, i) => (
                  <td key={o.id}>
                    <ContingencyTag
                      good={o.extractedData?.contingencies?.inspection?.waived ?? null}
                    />
                  </td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Sale of Property</td>
                {offers.map((o, i) => {
                  const val = o.extractedData?.contingencies?.sale_of_buyers_property ?? null;
                  return (
                    <td key={o.id}>
                      <ContingencyTag good={val === null ? null : !val} />
                    </td>
                  );
                })}
              </tr>

              <tr>
                <td className="cmp-row-label">Mortgage</td>
                {offers.map((o, i) => {
                  const val = o.extractedData?.contingencies?.mortgage ?? null;
                  return (
                    <td key={o.id}>
                      <ContingencyTag good={val === null ? null : !val} />
                    </td>
                  );
                })}
              </tr>

              {/* ── Timeline ── */}
              <SectionRow label="Timeline" colCount={n} />

              <tr>
                <td className="cmp-row-label">Closing Date</td>
                {offers.map((o, i) => (
                  <td key={o.id} className={bestClosingIdx.has(i) ? 'cmp-cell-best' : ''}>
                    {fmtDate(o.extractedData?.transaction?.closing_date)}
                    {bestClosingIdx.has(i) && <span className="cmp-cell-tag">Earliest</span>}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Offer Expires</td>
                {offers.map((o, i) => (
                  <td key={o.id}>{fmtDate(o.extractedData?.transaction?.offer_expiration_date)}</td>
                ))}
              </tr>

              {/* ── Parties ── */}
              <SectionRow label="Parties" colCount={n} />

              <tr>
                <td className="cmp-row-label">Buyer Agent</td>
                {offers.map((o, i) => (
                  <td key={o.id}>
                    <div>{o.extractedData?.agents?.buyer_broker?.agent_name || '—'}</div>
                    {o.extractedData?.agents?.buyer_broker?.agent_id && (
                      <div className="cmp-sub">Lic# {o.extractedData.agents.buyer_broker.agent_id}</div>
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Agent Company</td>
                {offers.map((o, i) => (
                  <td key={o.id}>{o.extractedData?.agents?.buyer_broker?.company || '—'}</td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Lender</td>
                {offers.map((o, i) => (
                  <td key={o.id}>
                    <div>{o.extractedData?.pre_approval?.lender_name || '—'}</div>
                    {o.extractedData?.pre_approval?.loan_officer_nmls && (
                      <div className="cmp-sub">NMLS# {o.extractedData.pre_approval.loan_officer_nmls}</div>
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="cmp-row-label">Lender Experience</td>
                {offers.map((o, i) => (
                  <td key={o.id}>
                    {o.lenderExperienceYears != null ? `${o.lenderExperienceYears.toFixed(1)} yrs` : '—'}
                  </td>
                ))}
              </tr>

              {/* ── Actions ── */}
              <tr className="cmp-actions-row">
                <td className="cmp-row-label" />
                {offers.map((o, i) => (
                  <td key={o.id}>
                    <button
                      className="cmp-view-btn"
                      onClick={() => { onClose(); navigate(`/offers/${o.id}`); }}
                    >
                      View Full Offer
                    </button>
                  </td>
                ))}
              </tr>

            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default OfferCompareModal;
